import type { ApiResult, RequestOptions } from '@/services/apiClient';
import { apiClient } from '@/services/apiClient';
import type {
  AgentDetail,
  AgentLog,
  AgentRecommendation,
  AgentRunTriggerResult,
  AgentSummary,
  AgentTask,
  AgentRun,
  Paginated,
  RecommendationStatus,
  MarketAdvisorRegistration,
} from './aiAgents.types';

/** Relative to `VITE_API_BASE_URL` (`…/api/v1`) — do not prefix another `/v1`. */
const BASE = '/agents';

export type AdvisorRegistrationOptions = RequestOptions & { sessionOrigin: string | null };

function read<T>(path: string, options?: RequestOptions) {
  return options === undefined ? apiClient.get<T>(path) : apiClient.get<T>(path, options);
}

function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export function aiAgentsErrorKind(status: number): 'permission' | 'offline' | 'unknown' {
  if (status === 403) return 'permission';
  if (status === 0) return 'offline';
  return 'unknown';
}

export const aiAgentsApi = {
  async registerMarketAdvisor(options: AdvisorRegistrationOptions) {
    return apiClient.post<MarketAdvisorRegistration, Record<string, never>>(
      `${BASE}/registry/market_intelligence_agent`,
      {},
      { ...options, sessionOrigin: options.sessionOrigin ?? null, allowRefresh: false },
    );
  },

  async listAgents(
    params: { page?: number; pageSize?: number; search?: string } = {},
    options?: RequestOptions,
  ) {
    return read<Paginated<AgentSummary>>(`${BASE}${queryString(params)}`, options);
  },

  async getAgent(id: string, options?: RequestOptions) {
    return read<AgentDetail>(`${BASE}/${encodeURIComponent(id)}`, options);
  },

  async updateAgent(
    id: string,
    input: { status?: AgentSummary['status']; autonomyLevel?: number },
    options?: RequestOptions,
  ) {
    const path = `${BASE}/${encodeURIComponent(id)}`;
    return options === undefined
      ? apiClient.patch<AgentDetail, typeof input>(path, input)
      : apiClient.patch<AgentDetail, typeof input>(path, input, options);
  },

  async listRecommendations(
    params: {
      page?: number;
      pageSize?: number;
      agentId?: string;
      status?: string;
      statuses?: readonly [RecommendationStatus, ...RecommendationStatus[]];
    } = {},
    options?: RequestOptions,
  ) {
    const { statuses, ...filters } = params;
    return read<Paginated<AgentRecommendation>>(
      `${BASE}/recommendations${queryString({ ...filters, statuses: statuses?.join(',') })}`,
      options,
    );
  },

  async recommendationAction(
    id: string,
    input: { action: 'APPROVE' | 'REJECT' | 'DISMISS'; reason?: string },
    options?: RequestOptions,
  ) {
    return apiClient.post<{ recommendation: AgentRecommendation }, typeof input>(
      `${BASE}/recommendations/${encodeURIComponent(id)}/actions`,
      input,
      options,
    );
  },

  async listTasks(agentId: string, params: { page?: number; pageSize?: number } = {}) {
    return apiClient.get<Paginated<AgentTask>>(
      `${BASE}/${encodeURIComponent(agentId)}/tasks${queryString(params)}`,
    );
  },

  async createTask(agentId: string, input: { title: string; payload?: Record<string, unknown> }) {
    return apiClient.post<AgentTask, typeof input>(
      `${BASE}/${encodeURIComponent(agentId)}/tasks`,
      input,
    );
  },

  async listRuns(agentId: string, params: { page?: number; pageSize?: number } = {}) {
    return apiClient.get<Paginated<AgentRun>>(
      `${BASE}/${encodeURIComponent(agentId)}/runs${queryString(params)}`,
    );
  },

  async triggerRun(
    agentId: string,
    input: { trigger?: 'MANUAL' | 'SCHEDULED' | 'EVENT'; projectId?: string } = {},
    options?: RequestOptions,
  ) {
    return apiClient.post<AgentRunTriggerResult, typeof input>(
      `${BASE}/${encodeURIComponent(agentId)}/generate-recommendations`,
      input,
      options,
    );
  },

  async listLogs(
    agentId: string,
    params: { page?: number; pageSize?: number; runId?: string } = {},
  ) {
    return apiClient.get<Paginated<AgentLog>>(
      `${BASE}/${encodeURIComponent(agentId)}/logs${queryString(params)}`,
    );
  },
};

export type AiAgentsApiResult<T> = ApiResult<T>;
